/**
 * Centralised JSON-LD schema builders for Arte Craft India SEO.
 * Each builder returns a plain object that <SeoHead jsonLd={[...]}/> stringifies
 * into a <script type="application/ld+json"> tag.
 *
 * Keep these builders pure and side-effect-free. Per-page SEO data files (e.g.
 * PomPomsSeo.ts) call them with product-specific args.
 */

import { SITE_BASE } from "./siteUrl";

const BASE = SITE_BASE;

// ─── Reusable Organization reference ──────────────────────────────────────────
export const ORGANIZATION_ID = `${BASE}/#organization`;
export const WEBSITE_ID = `${BASE}/#website`;

// ─── 1. LocalBusiness + Manufacturer + Organization ──────────────────────────
export function buildLocalBusinessSchema() {
    return {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": ["LocalBusiness", "Manufacturer", "Organization"],
                "@id": ORGANIZATION_ID,
                name: "Arte Craft India",
                legalName: "Arte Craft India Private Limited",
                url: BASE,
                logo: { "@type": "ImageObject", url: `${BASE}/android-chrome-512x512.png`, width: 512, height: 512 },
                image: `${BASE}/images/homepage-og.jpg`,
                description:
                    "Leading manufacturer and exporter of pom poms, tassels, fringes, faux fur pom poms, designer tassels, felt pom poms and beaded patch fringes — based in Delhi, India.",
                foundingDate: "2017",
                numberOfEmployees: { "@type": "QuantitativeValue", value: 75 },
                slogan: "Crafted in Delhi, Exported to the World",
                address: {
                    "@type": "PostalAddress",
                    streetAddress: "280/13 Gali No. 29 Molarband Extension",
                    addressLocality: "Badarpur, New Delhi",
                    addressRegion: "Delhi",
                    postalCode: "110044",
                    addressCountry: "IN",
                },
                geo: { "@type": "GeoCoordinates", latitude: 28.5045, longitude: 77.2855 },
                telephone: "+91-9818548369",
                email: "info@artecraftindia.com",
                openingHoursSpecification: [
                    {
                        "@type": "OpeningHoursSpecification",
                        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
                        opens: "09:30",
                        closes: "18:30",
                    },
                ],
                priceRange: "$$",
                currenciesAccepted: "INR, USD, EUR, GBP, AED",
                paymentAccepted: "Bank Transfer, Letter of Credit, PayPal, T/T",
                areaServed: [
                    { "@type": "Country", name: "India" },
                    { "@type": "Country", name: "United States" },
                    { "@type": "Country", name: "United Kingdom" },
                    { "@type": "Country", name: "Germany" },
                    { "@type": "Country", name: "France" },
                    { "@type": "Country", name: "United Arab Emirates" },
                    { "@type": "Country", name: "Australia" },
                    { "@type": "Country", name: "Canada" },
                ],
                knowsAbout: [
                    "Pom Poms",
                    "Tassels",
                    "Fringes",
                    "Faux Fur Pom Poms",
                    "Designer Tassels",
                    "Felt Pom Poms",
                    "Beaded Patch Fringes",
                    "Wholesale Manufacturing",
                    "Export from India",
                ],
                contactPoint: [
                    {
                        "@type": "ContactPoint",
                        telephone: "+91-9818548369",
                        contactType: "sales",
                        areaServed: ["IN", "US", "GB", "DE", "FR", "AE", "AU", "CA"],
                        availableLanguage: ["English", "Hindi"],
                    },
                ],
                sameAs: [
                    "https://www.facebook.com/artecraftindia",
                    "https://www.instagram.com/artecraftindia",
                    "https://www.linkedin.com/company/artecraftindia",
                    "https://twitter.com/artecraftindia",
                    "https://www.pinterest.com/artecraftindia",
                    "https://www.youtube.com/@artecraftindia",
                    "https://www.indiamart.com/artecraftindia",
                ],
            },
            {
                "@type": "WebSite",
                "@id": WEBSITE_ID,
                url: BASE,
                name: "Arte Craft India",
                publisher: { "@id": ORGANIZATION_ID },
                potentialAction: {
                    "@type": "SearchAction",
                    target: {
                        "@type": "EntryPoint",
                        urlTemplate: `${BASE}/search?q={search_term_string}`,
                    },
                    "query-input": "required name=search_term_string",
                },
            },
        ],
    };
}

// ─── 2. Product schema ────────────────────────────────────────────────────────
export interface ProductSchemaArgs {
    name: string;
    sku: string;
    canonicalUrl: string;
    imageSlug: string;
    description: string;
    material?: string;
    color?: string;
    sizes?: string[];
    category: string;
    lowPrice: string;
    highPrice: string;
    offerCount?: string;
    reviewCount?: string;
    ratingValue?: string;
}

export function buildProductSchema(args: ProductSchemaArgs) {
    return {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${args.canonicalUrl}#product`,
        name: args.name,
        image: [
            `${BASE}/assets/products/${args.imageSlug}-1x1.jpg`,
            `${BASE}/assets/products/${args.imageSlug}-4x3.jpg`,
            `${BASE}/assets/products/${args.imageSlug}-16x9.jpg`,
        ],
        description: args.description,
        sku: args.sku,
        mpn: `${args.sku}-DELHI`,
        category: args.category,
        brand: { "@type": "Brand", name: "Arte Craft India" },
        manufacturer: { "@id": ORGANIZATION_ID },
        ...(args.material && { material: args.material }),
        ...(args.color && { color: args.color }),
        ...(args.sizes && { size: args.sizes }),
        offers: {
            "@type": "AggregateOffer",
            priceCurrency: "INR",
            lowPrice: args.lowPrice,
            highPrice: args.highPrice,
            offerCount: args.offerCount ?? "50",
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@id": ORGANIZATION_ID },
        },
        ...(args.ratingValue && args.reviewCount && {
            aggregateRating: {
                "@type": "AggregateRating",
                ratingValue: args.ratingValue,
                reviewCount: args.reviewCount,
                bestRating: "5",
                worstRating: "1",
            },
        }),
    };
}

// ─── 3. BreadcrumbList ────────────────────────────────────────────────────────
export interface Crumb { name: string; url: string; }

export function buildBreadcrumbSchema(crumbs: Crumb[]) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.name,
            item: c.url,
        })),
    };
}

// ─── 4. FAQPage ───────────────────────────────────────────────────────────────
export interface FaqEntry { q: string; a: string; }

export function buildFAQSchema(faqs: FaqEntry[]) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
    };
}

// ─── 5. WebPage + Speakable ──────────────────────────────────────────────────
export function buildSpeakableSchema(canonicalUrl: string, title: string, imageSlug: string) {
    return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: title,
        speakable: {
            "@type": "SpeakableSpecification",
            cssSelector: [".speakable-summary", ".speakable-faq"],
        },
        primaryImageOfPage: `${BASE}/assets/products/${imageSlug}-hero.jpg`,
        datePublished: "2026-05-26",
        dateModified: "2026-05-26",
        inLanguage: "en-IN",
        isPartOf: { "@id": WEBSITE_ID },
        author: { "@id": ORGANIZATION_ID },
    };
}
